import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Optional sticky footer (e.g. action buttons). */
  footer?: ReactNode;
  className?: string;
}

/**
 * Nesting-safe body scroll lock. Counting open sheets means the lock is only
 * released once the LAST sheet closes, and it can never get stuck locked (every
 * open is paired with an unlock on close/unmount).
 */
let openSheetCount = 0;
function lockBodyScroll(): void {
  if (typeof document === 'undefined') return;
  if (openSheetCount === 0) document.body.style.overflow = 'hidden';
  openSheetCount += 1;
}
function unlockBodyScroll(): void {
  if (typeof document === 'undefined') return;
  openSheetCount = Math.max(0, openSheetCount - 1);
  if (openSheetCount === 0) document.body.style.overflow = '';
}

/**
 * A bottom-sheet modal. Rendered through a portal to <body> so it is never
 * trapped by an ancestor's positioning/stacking/overflow context (e.g. the
 * `.app-root > *` layering rules or a transformed parent) — a fixed overlay must
 * be relative to the viewport, not to whatever container happens to hold it.
 * Locks body scroll while open (nesting-safe) and closes on overlay click/Escape.
 */
export function Sheet({ open, onClose, title, children, footer, className }: SheetProps): JSX.Element | null {
  // Keep the latest onClose without re-running the lock effect on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', onKey);
      unlockBodyScroll();
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="as-sheet" role="dialog" aria-modal="true" aria-label={title}>
      <div className="as-sheet__overlay" onClick={onClose} />
      <div className={cn('as-sheet__panel', className)}>
        <div className="as-sheet__grip" />
        {title && <h2 className="as-sheet__title">{title}</h2>}
        <div className="as-sheet__body">{children}</div>
        {footer && <div className="as-sheet__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
